import { useBreakpointValue, useAuthenticator } from '@aws-amplify/ui-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { authClient } from '../utils/amplifyClient';
import * as Sentry from '@sentry/react';
import { useUserContext } from '../contexts/UserContext';
import { buildReportPath } from '../utils/reportUrlUtils';
import { KEYS_START_OF_DAY_REPORT, LOCAL_STORAGE_KEYS, S3_IMAGE_PATH, FORM_TYPES, ITEM_STATUS_CODES } from '../utils/constants';
import { v4 as uuidv4 } from 'uuid';

import { DashboardStats, RecentActivityItem } from '../types';
import { reportsByCompanyId, listDiscrepancyByCompanyIdAndUpdatedAt } from '../graphql/queries';
import { fetchDailyServiceReports, fetchInspectionReports } from '../graphql/reportQueries';
import { Company, Form as CompanyForm, ModelSortDirection, User } from '../types/schema';
import {
    deleteImageFromS3,
    formatDate,
    isDateAfter,
    resizeLogoFile,
    willExpireWithinAWeek,
} from '../utils/helperFunctions';
import { getLocalDayRange, isTimestampInLocalDayRange } from '../utils/dateInputUtils';
import {
    countAllTabEquipmentUnits,
    filterGroupedEquipmentByAssignmentScope,
    getEquipmentAssignmentScope,
} from '../utils/equipmentUnitUtils';
import '../css/DashboardContentUsers.css';
import { updateCompanyOwner } from '../graphql/mutations';
import { useCompanyContext } from '../contexts/CompanyContext';
import { useRegionAndLocationContext } from '../contexts/RegionAndLocationContext';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { useEquipmentContext } from '../contexts/EquipmentContext';
import { hasPermission, Permission } from '../utils/permissions';
import { useFormsContext } from '../contexts/FormsContext';
import { SafetyTrainingProvider } from '../contexts/SafetyTrainingContext';
import DailyTrainingPopup from './safety-training/DailyTrainingPopup';
import { LaunchDisplayModal } from './pages-daily-briefing/LaunchDisplayModal';
import type { BriefingTopic } from './pages-daily-briefing/dailyBriefingData';
import { findTodaysTopic, listBriefingTopics } from './pages-daily-briefing/dailyBriefingData';
import {
    setAutoShownDailyBrief,
    shouldAutoLaunchDailyBrief,
} from './pages-daily-briefing/dailyBriefingAutoLaunch';
import { ShiftReportWizard } from './ShiftReportWizard';
import { HomeFavoriteFormInspectionModal } from './HomeFavoriteFormInspectionModal';
import { Badge, Btn, Card, ImageUpload, InputField, MultiSelect, PageHeader, Pagination, StatsGrid, Scrollable, Stack } from './ui';
import { CustomModal } from './CustomModal';
import {
    IconAlertTriangle,
    IconAngleDown,
    IconAngleUp,
    IconClipboard,
    IconEquipment,
    IconQRcode,
    IconUsers,
} from '@icons';
import CardHeader from './ui/headers/CardHeader';
import { computeSummaryCounts, resolveFormType } from '../utils/reportUtils';
import {
    writeHomeFavoriteFormIds,
    MAX_HOME_DISPLAY_FAVORITES,
    hasStoredHomeFavoriteFormIds,
    resolveHomeFavoriteFormIds,
    getHomeDisplayFavoriteFormIds,
    getHomeFavoriteFormMetaLabel,
    getHomeEquipmentInspectHref,
    handleHomeEquipmentInspectClick,
    findHomeFavoriteFormCategoryImage,
    getHomeFavoriteFormImageKey,
} from '../utils/homeFormFavorites';

const client = authClient;
const RECENT_ACTIVITY_LIMIT = 20;
const DASHBOARD_ISSUES_PREVIEW_LIMIT = 25;
const DASHBOARD_EQUIPMENT_PAGE_SIZE = 10;

const getStoredEquipmentListExpanded = (): boolean => {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.HOME_EQUIPMENT_LIST_EXPANDED) === 'true';
};

const setStoredEquipmentListExpanded = (expanded: boolean) => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.HOME_EQUIPMENT_LIST_EXPANDED, String(expanded));
};

const getStartOfYesterday = () => {
    const start = new Date();
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    return start;
};

const getRecentActivityDateRange = () => {
    const end = new Date();

    return {
        dateStart: getStartOfYesterday().toISOString(),
        dateEnd: end.toISOString(),
    };
};

const formatActivityDate = (isoString: string): string => {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
    const hours12 = date.getHours() % 12 || 12;
    const hours = String(hours12).padStart(2, '0');

    return `${hours}:${minutes} ${ampm}`;
};

const getInitials = (name?: string | null): string => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0]?.[0] || '?').toUpperCase();
};

const deriveActivityLabel = (
    type: 'inspection' | 'dailyService',
    inopCount?: number | null,
    warnCount?: number | null,
): string => {
    if ((inopCount ?? 0) > 0) {
        return 'Critical issue reported';
    }
    if ((warnCount ?? 0) > 0) {
        return 'Issue reported';
    }
    return type === 'dailyService' ? 'Daily service completed' : 'Inspection completed';
};

const buildActivityTitle = (
    type: 'inspection' | 'dailyService',
    unitName?: string | null,
    equipmentName?: string | null,
): string => {
    const subject = unitName || equipmentName || 'Unknown Equipment';
    return type === 'dailyService' ? `Daily Service — ${subject}` : `Inspection — ${subject}`;
};

const buildIssuesByReportId = (
    discrepancies: Array<{ id: string; reportId?: string | null; status?: string | null }>,
): Map<string, string> => {
    const priority = { inop: 1, warn: 2, under_review: 3 };
    const bestByReportId = new Map<string, { id: string; priority: number }>();

    discrepancies.forEach((issue) => {
        if (!issue.reportId) {
            return;
        }

        const issuePriority = priority[issue.status as keyof typeof priority] ?? 999;
        const existing = bestByReportId.get(issue.reportId);

        if (!existing || issuePriority < existing.priority) {
            bestByReportId.set(issue.reportId, { id: issue.id, priority: issuePriority });
        }
    });

    return new Map([...bestByReportId.entries()].map(([reportId, issue]) => [reportId, issue.id]));
};

const buildRecentActivity = (
    inspectionReports: Array<Record<string, unknown>>,
    dailyServiceReports: Array<Record<string, unknown>>,
    legacyReports: Array<Record<string, unknown>>,
    usersById: Map<string, User>,
    issuesByReportId: Map<string, string>,
): RecentActivityItem[] => {
    const activityStart = getStartOfYesterday();

    const inspectionActivity = inspectionReports.map((report) => {
        const userId = String(report.userId ?? '');
        const user = usersById.get(userId);

        return {
            id: String(report.id),
            userId,
            userName: String(report.userName ?? user?.name ?? 'Unknown User'),
            userPhoto: user?.userPhoto,
            title: buildActivityTitle(
                'inspection',
                report.unitName as string | null | undefined,
                report.equipmentCategoryName as string | null | undefined,
            ),
            equipmentName: String(report.unitName ?? report.equipmentCategoryName ?? 'Unknown Equipment'),
            status: deriveActivityLabel('inspection', report.inopCount as number, report.warnCount as number),
            source: 'InspectionReport' as const,
            issueId: issuesByReportId.get(String(report.id)),
            createdAt: String(report.createdAt ?? report.inspectionCompleted ?? ''),
        };
    });

    const dailyServiceActivity = dailyServiceReports.map((report) => {
        const userId = String(report.userId ?? '');
        const user = usersById.get(userId);

        return {
            id: String(report.id),
            userId,
            userName: String(report.userName ?? user?.name ?? 'Unknown User'),
            userPhoto: user?.userPhoto,
            title: buildActivityTitle(
                'dailyService',
                report.unitName as string | null | undefined,
                report.equipmentCategoryName as string | null | undefined,
            ),
            equipmentName: String(report.unitName ?? report.equipmentCategoryName ?? 'Unknown Equipment'),
            status: deriveActivityLabel('dailyService', report.inopCount as number, report.warnCount as number),
            source: 'DailyServiceReport' as const,
            issueId: issuesByReportId.get(String(report.id)),
            createdAt: String(report.createdAt ?? report.inspectionCompleted ?? ''),
        };
    });

    const legacyActivity = legacyReports
        .filter((report) => report.equipmentId !== KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID)
        .map((report) => {
            const userId = String(report.userId ?? '');
            const user = usersById.get(userId);
            let counts = { inopCount: 0, warnCount: 0 };

            try {
                const prompts = JSON.parse(String(report.userData ?? '[]'));
                if (Array.isArray(prompts)) {
                    counts = computeSummaryCounts(prompts);
                }
            } catch {
                // Ignore malformed legacy report payloads
            }

            return {
                id: String(report.id),
                userId,
                userName: String(report.name ?? user?.name ?? 'Unknown User'),
                userPhoto: user?.userPhoto,
                title: buildActivityTitle(
                    report.dailyServiceLogReport ? 'dailyService' : 'inspection',
                    report.unitName as string | null | undefined,
                    report.equipmentName as string | null | undefined,
                ),
                equipmentName: String(report.unitName ?? report.equipmentName ?? 'Unknown Equipment'),
                status: deriveActivityLabel(
                    report.dailyServiceLogReport ? 'dailyService' : 'inspection',
                    counts.inopCount,
                    counts.warnCount,
                ),
                source: 'LegacyReport' as const,
                issueId: issuesByReportId.get(String(report.id)),
                createdAt: String(report.createdAt ?? ''),
            };
        });

    return [...inspectionActivity, ...dailyServiceActivity, ...legacyActivity]
        .filter((item) => item.createdAt && new Date(item.createdAt) >= activityStart)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, RECENT_ACTIVITY_LIMIT);
};

export const CompanyHomepage = () => {
    const [pausedSubscription, setPausedSubscription] = useState<boolean>(false);
    const [willExpire, setWillExpire] = useState<boolean>(false);
    const [_expirationDate, setExpirationDate] = useState<string>('');
    const [isEditCompanyModelOpen, setIsEditCompanyModelOpen] = useState(false);
    const [companyToEdit, setCompanyToEdit] = useState<Company | null>(null);
    const [companyNameInForm, setCompanyNameInForm] = useState('');
    const [companyLogoInForm, setCompanyLogoInForm] = useState<File[] | []>([]);
    const [companyDescriptionInForm, setCompanyDescriptionInForm] = useState('');
    const [companyTypeInForm, setCompanyTypeInForm] = useState('');
    const [companyLogoPreview, setCompanyLogoPreview] = useState('');
    const [isFreeDemo, setIsFreeDemo] = useState<boolean>(false);
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [_subscriptionType, setSubscriptionType] = useState('');
    const [_maxNumberUsers, setMaxNumberUsers] = useState(99999);
    const [_maxNumberEquipment, setMaxNumberEquipment] = useState(99999);
    const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});
    const [activityUserPhotoUrls, setActivityUserPhotoUrls] = useState<Record<string, string>>({});
    const [isSubscriptionExpiredModalOpen, setIsSubscriptionExpiredModalOpen] = useState(false);

    const isMobile = useBreakpointValue({
        base: true,
        medium: false,
        large: false,
    }) as boolean;

    const [showEquipmentTiles, setShowEquipmentTiles] = useState(getStoredEquipmentListExpanded);
    const [equipmentPage, setEquipmentPage] = useState(1);
    const [showDailyTrainingPopup, setShowDailyTrainingPopup] = useState(true);
    const [autoBriefTopic, setAutoBriefTopic] = useState<BriefingTopic | null>(null);
    const [autoBriefVisible, setAutoBriefVisible] = useState(false);
    const [autoBriefResolved, setAutoBriefResolved] = useState(false);
    const [shiftReportModal, setShiftReportModal] = useState<{
        open: boolean;
        formName: string;
        formId?: string;
        shiftType: 'start-of-day' | 'end-of-day';
    }>({ open: false, formName: '', shiftType: 'start-of-day' });
    const [favoriteFormInspectionModal, setFavoriteFormInspectionModal] = useState<{
        open: boolean;
        form: CompanyForm | null;
    }>({ open: false, form: null });
    const [favoriteFormIds, setFavoriteFormIds] = useState<string[]>([]);

    // Add responsive detection

    const resetCompanyForm = async () => {
        setCompanyNameInForm('');
        setCompanyTypeInForm('');
        setCompanyDescriptionInForm('');
        setCompanyLogoInForm([]);
        // setEditRegions([]);
        // setRegions([{ name: '', description: '' }]);
    };

    const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
        activeUsersToday: 0,
        equipmentInspectedToday: 0,
        totalIssuesToday: 0,
        currentIssues: [],
        totalUnits: 0,
        recentActivity: [],
    });

    const navigate = useNavigate();
    const { user: authUser } = useAuthenticator((context) => [context.user]);

    const { company, companyUser, companyOwner, businessAdmin, allCompanyUsers } = useUserContext();
    const { updateCompanyInDatabase } = useCompanyContext();
    const { companyGroupedEquipmentData, refreshEquipment, companyUnits } = useEquipmentContext();
    const { companyLocations, companyRegions } = useRegionAndLocationContext();
    const { companyForms, companyInspectionForms, companyDailyServiceForms } = useFormsContext();

    const favoriteScopeUserId =
        companyUser?.id || companyOwner?.id || authUser?.userId || null;
    const favoriteScopeCompanyId = company?.id || companyOwner?.companyId || null;

    useEffect(() => {
        let cancelled = false;

        const resolveAutoBrief = async () => {
            if (!company?.id || !companyUser?.id) {
                if (!cancelled) {
                    setAutoBriefTopic(null);
                    setAutoBriefVisible(false);
                    setAutoBriefResolved(true);
                }
                return;
            }

            if (!cancelled) setAutoBriefResolved(false);

            try {
                const topics = await listBriefingTopics(company.id);
                if (cancelled) return;
                const todayTopic = findTodaysTopic(topics, company.id);
                if (
                    shouldAutoLaunchDailyBrief({
                        topic: todayTopic,
                        user: companyUser,
                        companyId: company.id,
                    })
                ) {
                    setAutoBriefTopic(todayTopic);
                    setAutoBriefVisible(true);
                } else {
                    setAutoBriefTopic(null);
                    setAutoBriefVisible(false);
                }
            } catch (err) {
                console.error('Failed to resolve Daily Brief auto-launch', err);
                Sentry.captureException(err);
                if (!cancelled) {
                    setAutoBriefTopic(null);
                    setAutoBriefVisible(false);
                }
            } finally {
                if (!cancelled) setAutoBriefResolved(true);
            }
        };

        void resolveAutoBrief();
        return () => {
            cancelled = true;
        };
    }, [company?.id, companyUser]);

    const equipmentAssignmentScope = useMemo(
        () => getEquipmentAssignmentScope(companyUser, companyLocations, companyRegions, companyOwner, businessAdmin),
        [companyUser, companyLocations, companyRegions, companyOwner, businessAdmin],
    );

    const totalEquipmentUnits = useMemo(() => {
        return countAllTabEquipmentUnits(companyGroupedEquipmentData, equipmentAssignmentScope);
    }, [companyGroupedEquipmentData, equipmentAssignmentScope]);

    const startOfDayForm = useMemo(
        () => companyForms.find((f) => f.name?.toLowerCase().startsWith('start of day')),
        [companyForms],
    );
    const endOfDayForm = useMemo(
        () => companyForms.find((f) => f.name?.toLowerCase().startsWith('end of day')),
        [companyForms],
    );

    const favoriteFormOptionGroups = useMemo(() => {
        const toOption = (form: (typeof companyInspectionForms)[number]) => ({
            value: form.id as string,
            label: form.name || 'Untitled form',
        });

        const inspectionById = new Map<string, { value: string; label: string }>();

        for (const form of companyInspectionForms) {
            if (!form.id || (form as { status?: string | null }).status === 'draft') {
                continue;
            }
            inspectionById.set(form.id, toOption(form));
        }

        for (const form of [startOfDayForm, endOfDayForm]) {
            if (!form?.id || inspectionById.has(form.id)) {
                continue;
            }
            if ((form as { status?: string | null }).status === 'draft') {
                continue;
            }
            inspectionById.set(form.id, toOption(form));
        }

        const dailyServiceOptions = companyDailyServiceForms
            .filter((form) => form.id && (form as { status?: string | null }).status !== 'draft')
            .map(toOption)
            .sort((a, b) => a.label.localeCompare(b.label));

        const inspectionOptions = Array.from(inspectionById.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );

        const groups: { label: string; options: { value: string; label: string }[] }[] = [];

        if (inspectionOptions.length > 0) {
            groups.push({ label: 'Inspection Forms', options: inspectionOptions });
        }

        if (dailyServiceOptions.length > 0) {
            groups.push({ label: 'Daily Service Forms', options: dailyServiceOptions });
        }

        return groups;
    }, [companyDailyServiceForms, companyInspectionForms, endOfDayForm, startOfDayForm]);

    const favoriteFormOptions = useMemo(
        () => favoriteFormOptionGroups.flatMap((group) => group.options),
        [favoriteFormOptionGroups],
    );

    const defaultFavoriteFormIds = useMemo(() => {
        return [startOfDayForm?.id, endOfDayForm?.id].filter((id): id is string => Boolean(id));
    }, [endOfDayForm?.id, startOfDayForm?.id]);

    const homeDisplayFavoriteFormIds = useMemo(
        () => getHomeDisplayFavoriteFormIds(favoriteFormIds),
        [favoriteFormIds],
    );

    const favoriteForms = useMemo(() => {
        const byId = new Map<string, (typeof companyForms)[number]>();
        for (const form of [...companyForms, ...companyInspectionForms, ...companyDailyServiceForms]) {
            if (form.id) {
                byId.set(form.id, form);
            }
        }

        return homeDisplayFavoriteFormIds
            .map((id) => byId.get(id))
            .filter((form): form is NonNullable<typeof form> =>
                Boolean(form && (form as { status?: string | null }).status !== 'draft'),
            );
    }, [companyDailyServiceForms, companyForms, companyInspectionForms, homeDisplayFavoriteFormIds]);

    const scopedEquipment = useMemo(
        () => filterGroupedEquipmentByAssignmentScope(companyGroupedEquipmentData, equipmentAssignmentScope),
        [companyGroupedEquipmentData, equipmentAssignmentScope],
    );

    const filteredEquipment = scopedEquipment;

    const handleFavoriteFormsChange = (formIds: string[]) => {
        const displayIds = formIds.slice(0, MAX_HOME_DISPLAY_FAVORITES);
        const displaySet = new Set(displayIds);
        const remaining = favoriteFormIds.filter((id) => !displaySet.has(id));
        setFavoriteFormIds(
            writeHomeFavoriteFormIds([...displayIds, ...remaining], favoriteScopeCompanyId, favoriteScopeUserId),
        );
    };

    const openShiftReport = (
        formName: string,
        shiftType: 'start-of-day' | 'end-of-day',
        formId?: string,
    ) => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED, new Date().toISOString());
        setShiftReportModal({
            open: true,
            formName,
            formId,
            shiftType,
        });
    };

    const handleFavoriteFormClick = (formId: string) => {
        const form = favoriteForms.find((item) => item.id === formId);
        if (!form) {
            return;
        }

        const formType = resolveFormType(form);
        const lowerName = form.name?.toLowerCase() || '';

        if (
            form.id === startOfDayForm?.id ||
            formType === FORM_TYPES.SHIFT_REPORT ||
            lowerName.startsWith('start of day') ||
            lowerName.startsWith('end of day')
        ) {
            const shiftType: 'start-of-day' | 'end-of-day' =
                form.id === endOfDayForm?.id || lowerName.startsWith('end of day') ? 'end-of-day' : 'start-of-day';
            openShiftReport(
                form.name || (shiftType === 'end-of-day' ? 'end of day' : 'start of day'),
                shiftType,
                form.id as string,
            );
            return;
        }

        setFavoriteFormInspectionModal({ open: true, form });
    };

    useEffect(() => {
        if (!favoriteScopeCompanyId || !favoriteScopeUserId) {
            return;
        }

        setFavoriteFormIds((current) => {
            if (hasStoredHomeFavoriteFormIds(favoriteScopeCompanyId, favoriteScopeUserId)) {
                return resolveHomeFavoriteFormIds(
                    favoriteScopeCompanyId,
                    favoriteScopeUserId,
                    defaultFavoriteFormIds,
                );
            }

            if (current.length > 0) {
                return current;
            }

            return resolveHomeFavoriteFormIds(
                favoriteScopeCompanyId,
                favoriteScopeUserId,
                defaultFavoriteFormIds,
            );
        });
    }, [defaultFavoriteFormIds, favoriteScopeCompanyId, favoriteScopeUserId]);

    useEffect(() => {
        const loadData = async () => {
            if (company?.id || companyOwner?.companyId) {
                await refreshEquipment();
            }
        };

        loadData();
    }, [company?.id, companyOwner?.companyId]);

    useEffect(() => {
        if (company?.id) {
            fetchDashboardStats();
        }
    }, [company?.id, companyUnits.length, allCompanyUsers.length, companyGroupedEquipmentData.length]);

    const toggleEquipmentTiles = () => {
        setShowEquipmentTiles((prev) => {
            const next = !prev;
            setStoredEquipmentListExpanded(next);
            return next;
        });
    };

    const getCompanyData = async () => {
        try {
            const today = new Date();
            const formattedToday = formatDate(today);
            setIsExpired(isDateAfter(formatDate(new Date(company?.subscriptionExpiration || '')), formattedToday));
            setWillExpire(
                willExpireWithinAWeek(formatDate(new Date(company?.subscriptionExpiration || '')), formattedToday),
            );
        } catch (error) {
            console.error('error :>> ', error);
            Sentry.captureException(error);
        }
    };

    const equipmentTotalPages = Math.max(1, Math.ceil(filteredEquipment.length / DASHBOARD_EQUIPMENT_PAGE_SIZE));

    const displayEquipment = useMemo(() => {
        if (!isMobile) {
            return filteredEquipment;
        }

        const start = (equipmentPage - 1) * DASHBOARD_EQUIPMENT_PAGE_SIZE;
        return filteredEquipment.slice(start, start + DASHBOARD_EQUIPMENT_PAGE_SIZE);
    }, [filteredEquipment, equipmentPage, isMobile]);

    const showEquipmentPagination = isMobile && equipmentTotalPages > 1;

    useEffect(() => {
        setEquipmentPage(1);
    }, [filteredEquipment, isMobile]);

    useEffect(() => {
        if (isMobile && equipmentPage > equipmentTotalPages) {
            setEquipmentPage(equipmentTotalPages);
        }
    }, [equipmentPage, equipmentTotalPages, isMobile]);

    useEffect(() => {
        // Category images for favorite form cards (company equipment, not user scope)
        favoriteForms.forEach((form) => {
            if (!form.id) {
                return;
            }

            const imageKey = getHomeFavoriteFormImageKey(form.id);
            if (imageUrls[imageKey]) {
                return;
            }

            const match = findHomeFavoriteFormCategoryImage(companyGroupedEquipmentData, form);
            if (match) {
                getEquipmentImageUrl(match.imagePath, match.imageKey);
            }
        });
    }, [companyGroupedEquipmentData, favoriteForms, imageUrls]);

    useEffect(() => {
        // Load images for filtered equipment
        filteredEquipment.forEach((item) => {
            if (item.image && item.image.split('/')[0] === 'public') {
                getEquipmentImageUrl(item.image, item.id);
            }
        });
    }, [filteredEquipment]);

    const updateSubscription = async () => {
        const companyOwnerId = company?.companyOwnerId;
        try {
            // Update company owner
            const companyOwnerInput = {
                id: companyOwnerId as string,
                subscriptionType: 'paused',
            };
            await client.graphql({ query: updateCompanyOwner, variables: { input: companyOwnerInput } });

            await updateCompanyInDatabase(company?.id as string, { subscriptionType: 'paused' });

            setPausedSubscription(true);
        } catch (error) {
            console.error('Error updating subscription:', error);
        }
    };

    const getEquipmentImageUrl = async (imagePath: string, equipmentId: string) => {
        try {
            if (!imageUrls[equipmentId] && imagePath) {
                const photoUrl = await getUrl({
                    path: imagePath,
                });
                setImageUrls((prev) => ({
                    ...prev,
                    [equipmentId]: photoUrl.url.href,
                }));
            }
        } catch (error) {
            console.error('Error fetching image:', error);
            Sentry.captureException(error);
        }
    };

    const loadActivityUserPhotos = async (activities: RecentActivityItem[]) => {
        const photoUpdates: Record<string, string> = {};

        await Promise.all(
            activities.map(async (activity) => {
                if (!activity.userPhoto || activityUserPhotoUrls[activity.userId]) {
                    return;
                }

                try {
                    const photoUrl = await getUrl({ path: activity.userPhoto });
                    photoUpdates[activity.userId] = photoUrl.url.href;
                } catch (error) {
                    console.error('Error fetching activity user photo:', error);
                }
            }),
        );

        if (Object.keys(photoUpdates).length > 0) {
            setActivityUserPhotoUrls((prev) => ({ ...prev, ...photoUpdates }));
        }
    };

    useEffect(() => {
        if (company !== null && company?.id) {
            setPausedSubscription(company.subscriptionType === 'paused');

            const companyExpirationDate = company.subscriptionExpiration as string;
            setExpirationDate(company.subscriptionExpiration as string);

            const today = new Date();
            const formattedToday = formatDate(today);

            const isExpired = isDateAfter(companyExpirationDate, formattedToday);
            const willExpireWithinWeek = willExpireWithinAWeek(companyExpirationDate, formattedToday);

            if (isExpired && company.subscriptionType !== 'paused') {
                updateSubscription();
            } else {
                setWillExpire(willExpireWithinWeek);
            }
        }
    }, [company]);

    const [, setCompanyLogo] = useState('');

    useEffect(() => {
        if (company?.logo?.split('/')[0] === 'public') {
            getCompanyLogo(company.logo);
        } else {
            setCompanyLogo(company?.logo ? company?.logo : './site-admin-img.png');
        }
    }, [company]);

    const getCompanyLogo = async (imagePath: string) => {
        try {
            const photoUrl = await getUrl({
                path: imagePath,
            });
            setCompanyLogo(photoUrl.url.href);
            setCompanyLogoPreview(photoUrl.url.href);
        } catch (error) {
            console.error('Error fetching image:', error);
            Sentry.captureException(error);
        }
    };

    const fetchDashboardStats = async () => {
        if (!company?.id) return;

        try {
            const todayRange = getLocalDayRange();
            const recentActivityRange = getRecentActivityDateRange();

            const [
                inspectionReports,
                dailyServiceReports,
                legacyReportsResponse,
                discrepanciesResponse,
                recentInspectionReports,
                recentDailyServiceReports,
                recentLegacyReportsResponse,
            ] = await Promise.all([
                fetchInspectionReports(company.id, todayRange),
                fetchDailyServiceReports(company.id, todayRange),
                client.graphql({
                    query: reportsByCompanyId,
                    variables: {
                        companyId: company.id,
                        createdAt: { between: [todayRange.dateStart, todayRange.dateEnd] },
                        sortDirection: ModelSortDirection.DESC,
                    },
                }),
                client.graphql({
                    query: listDiscrepancyByCompanyIdAndUpdatedAt,
                    variables: {
                        companyId: company.id,
                        sortDirection: ModelSortDirection.DESC,
                    },
                }),
                fetchInspectionReports(company.id, recentActivityRange),
                fetchDailyServiceReports(company.id, recentActivityRange),
                client.graphql({
                    query: reportsByCompanyId,
                    variables: {
                        companyId: company.id,
                        createdAt: {
                            between: [recentActivityRange.dateStart, recentActivityRange.dateEnd],
                        },
                        sortDirection: ModelSortDirection.DESC,
                    },
                }),
            ]);

            const legacyReportsToday = legacyReportsResponse.data.reportsByCompanyId.items.filter(
                (report) => report.equipmentId !== KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID,
            );

            const discrepancies = discrepanciesResponse.data.listDiscrepancyByCompanyIdAndUpdatedAt.items;

            const reportDiscrepancies = discrepancies
                .filter((issue) => issue.equipmentId !== KEYS_START_OF_DAY_REPORT.EQUIPMENT_ID)
                .filter((issue) => issue.status !== ITEM_STATUS_CODES.ok)
                .sort((a, b) => {
                    // Priority order: inop (1), warn (2), under_review (3)
                    const priority = { inop: 1, warn: 2, under_review: 3 };
                    const aPriority = priority[a.status as keyof typeof priority] || 999;
                    const bPriority = priority[b.status as keyof typeof priority] || 999;
                    return aPriority - bPriority;
                });

            const todayActivityReports = [...inspectionReports, ...dailyServiceReports, ...legacyReportsToday];

            // Unique users who submitted an inspection or daily service report today
            const activeUsersToday = new Set(todayActivityReports.map((report) => report.userId).filter(Boolean)).size;

            // Unique units inspected/serviced today (legacy reports may only have equipmentId)
            const equipmentInspectedToday = new Set(
                todayActivityReports
                    // `equipmentCategoryId` is a residual Gen1 field absent from Gen2 Report types;
                    // it resolves to undefined at runtime, preserving prior behavior.
                    .map(
                        (report) =>
                            report.unitId ||
                            report.equipmentId ||
                            (report as { equipmentCategoryId?: string | null }).equipmentCategoryId,
                    )
                    .filter(Boolean),
            ).size;

            const totalIssuesToday = discrepancies.filter((issue) =>
                isTimestampInLocalDayRange(issue.createdAt, todayRange),
            ).length;

            const usersById = new Map(allCompanyUsers.map((user) => [user.id, user]));
            const issuesByReportId = buildIssuesByReportId(discrepancies);
            const recentActivity = buildRecentActivity(
                recentInspectionReports,
                recentDailyServiceReports,
                recentLegacyReportsResponse.data.reportsByCompanyId.items,
                usersById,
                issuesByReportId,
            );

            setDashboardStats((prev) => ({
                ...prev,
                activeUsersToday,
                equipmentInspectedToday,
                totalIssuesToday,
                currentIssues: reportDiscrepancies,
                recentActivity,
            }));

            void loadActivityUserPhotos(recentActivity);
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
        }
    };

    const handleActivityClick = (activity: RecentActivityItem) => {
        if (activity.issueId) {
            navigate(`/issue/${activity.issueId}`);
            return;
        }

        navigate(
            buildReportPath({
                id: activity.id,
                source: activity.source,
                slugLabel: activity.equipmentName,
            }),
        );
    };

    const handleIssueClick = (issue: DashboardStats['currentIssues'][number]) => {
        navigate(`/issue/${issue.id}`);
    };

    const handleEditFormSubmit = async (e: { preventDefault: () => void }) => {
        e.preventDefault();
        if (!companyToEdit) return;
        if (!companyOwner) return;

        try {
            let imagePath = companyToEdit?.logo;
            if (companyToEdit?.logo && !companyLogoInForm) {
                //handles delete if user clicks delete
                try {
                    if (companyToEdit?.logo && companyToEdit.logo.split('/')[0] === 'public') {
                        await deleteImageFromS3(companyToEdit.logo);
                    }
                    imagePath = '';
                } catch (error) {
                    console.error('Error deleting file from S3:', error);
                    Sentry.captureException(error);
                }
            }
            // Handle new image upload if there's a new photo
            // Check if there's a new photo to upload, regardless of whether there was a previous image
            if (companyLogoInForm.length > 0) {
                // Only try to delete old image if one exists
                if (companyToEdit?.logo) {
                    try {
                        await deleteImageFromS3(companyToEdit.logo);
                    } catch (error) {
                        console.error('Error deleting file from S3:', error);
                        Sentry.captureException(error);
                    }
                }
                // Upload new image
                const resizedImage = await resizeLogoFile(companyLogoInForm[0]);
                const imageFile = resizedImage as File;
                const fileName = `${uuidv4()}-LOGO-${companyNameInForm || companyToEdit?.name}-${imageFile.name}`;

                const newImageS3 = await uploadData({
                    path: S3_IMAGE_PATH.companyLogoImagePath(companyOwner.id, fileName),
                    data: imageFile,
                }).result;

                imagePath = newImageS3.path;
            }

            // const input: UpdateCompanyInput = {
            //   id: companyToEdit.id,
            //   name: companyNameInForm.trim() ? companyNameInForm.trim() : companyToEdit.name,
            //   type: companyTypeInForm.trim() ? companyTypeInForm.trim() : companyToEdit.type,
            //   logo: imagePath,
            //   description: companyDescriptionInForm ? companyDescriptionInForm.trim() : companyToEdit.description,
            // }
            // await client.graphql({
            //   query: updateCompanyMutation,
            //   variables: { input },
            // });

            await updateCompanyInDatabase(companyToEdit.id, {
                name: companyNameInForm.trim() ? companyNameInForm.trim() : companyToEdit.name,
                type: companyTypeInForm.trim() ? companyTypeInForm.trim() : companyToEdit.type,
                logo: imagePath,
                description: companyDescriptionInForm ? companyDescriptionInForm.trim() : companyToEdit.description,
            });

            resetCompanyForm();
            // getCompanyData();
        } catch (error) {
            console.error('error :>> ', error);
            Sentry.captureException(error);
        } finally {
            setIsEditCompanyModelOpen(false);
        }
    };

    const getCompanyOwnerData = async () => {
        if (companyOwner) {
            setSubscriptionType(companyOwner.subscriptionType);
            setIsFreeDemo(companyOwner.subscriptionType === 'free-demo');
            setPausedSubscription(companyOwner.subscriptionType === 'paused');
            const currentDate = new Date();
            const newExpirationDate = new Date(currentDate);
            newExpirationDate.setDate(currentDate.getDate() + 30);
            setExpirationDate(newExpirationDate.toLocaleString().split(',')[0]);
            if (companyOwner.subscriptionType === 'free-demo') {
                setMaxNumberUsers(4);
                setMaxNumberEquipment(4);
            } else if (companyOwner.subscriptionType === 'paid-limited-access') {
                setMaxNumberUsers(10);
                setMaxNumberEquipment(10);
            }
        } else {
            setPausedSubscription(true);
        }
    };

    useEffect(() => {
        if (companyOwner) {
            getCompanyOwnerData();
            getCompanyData();
        }
    }, [company, companyOwner]);

    const getDashboardStatCards = (stats: DashboardStats) => [
        { icon: <IconUsers />, value: stats.activeUsersToday, label: 'Active users today' },
        { icon: <IconClipboard />, value: stats.equipmentInspectedToday, label: 'Equipment inspected today' },
        { icon: <IconAlertTriangle />, value: stats.totalIssuesToday, label: 'Issues today' },
        { icon: <IconEquipment />, value: totalEquipmentUnits, label: 'Total active company units' },
    ];

    return (
        <Stack direction="vertical" size="m">
            {/* Daily Brief auto-launch (Home, once per local calendar day) */}
            {autoBriefTopic ? (
                <LaunchDisplayModal
                    visible={autoBriefVisible}
                    topic={autoBriefTopic}
                    autoMode
                    autoFullscreen
                    onAutoSessionComplete={() => {
                        if (company?.id && companyUser?.id) {
                            setAutoShownDailyBrief(companyUser.id, company.id);
                        }
                    }}
                    onDismiss={() => {
                        setAutoBriefVisible(false);
                    }}
                />
            ) : null}
            {/* Daily Training Popup for enrolled users — deferred until Daily Brief auto-overlay finishes */}
            {company?.trainingModuleEnrollment &&
                showDailyTrainingPopup &&
                autoBriefResolved &&
                !autoBriefVisible && (
                <SafetyTrainingProvider>
                    <DailyTrainingPopup onClose={() => setShowDailyTrainingPopup(false)} />
                </SafetyTrainingProvider>
            )}
            {/* <Header variant="h1" id="page-top" >
        Welcome {(companyUser || companyOwner)?.name}!
      </Header> */}
            {pausedSubscription || isExpired ? (
                <div className="home-subscription-banner home-subscription-banner--danger" role="status">
                    Please Contact Admin to Resume Subscription.{' '}
                    {hasPermission(companyOwner, companyUser, Permission.MANAGE_USERS, businessAdmin) && (
                        <Btn
                            variant="link"
                            buttonText="Click for Info"
                            buttonOnClick={() => navigate('/manage/subscription')}
                        />
                    )}
                </div>
            ) : !isFreeDemo && willExpire ? (
                <div className="home-subscription-banner home-subscription-banner--warning" role="status">
                    Warning Subscription due to Expire on <strong>{company?.subscriptionExpiration}</strong>{' '}
                    {hasPermission(companyOwner, companyUser, Permission.MANAGE_USERS, businessAdmin) && (
                        <Btn
                            variant="link"
                            buttonText="Click for Info"
                            buttonOnClick={() => navigate('/manage/subscription')}
                        />
                    )}
                </div>
            ) : (
                isFreeDemo && (
                    <div className="home-subscription-banner home-subscription-banner--warning" role="status">
                        Paid Subscription Required for full feature access{' '}
                        <Btn
                            variant="link"
                            buttonText="Upgrade Here"
                            buttonOnClick={() => navigate('/manage/subscription')}
                        />
                        .
                    </div>
                )
            )}

            {/* this is new content */}

            <div className="page-container dashboard-container">
                {/* Welcome Section */}
                <PageHeader
                    // title={`Welcome ${(companyUser || companyOwner)?.name}!`}
                    title={`Welcome`}
                    actions={<span className="pill">{new Date().toLocaleDateString()}</span>}
                />

                {/* Stats grid */}
                <StatsGrid
                    stats={getDashboardStatCards(dashboardStats).map(({ icon, value, label }) => (
                        <div key={label} className="stat-item">
                            <div className="stat-row">
                                <div className="stat-icon">{icon}</div>

                                <div className="stat-value">{value}</div>
                            </div>
                            <div className="stat-row">
                                <div className="stat-label">{label}</div>
                            </div>
                        </div>
                    ))}
                />

                {/* Main Content Grid */}
                <div className="main-content-grid">
                    {/* Left Column - Inspections */}
                    <Card
                        variant="card-lg"
                        className={`inspection-section ${showEquipmentTiles ? 'equipment-list-expanded' : 'equipment-list-collapsed'}`}
                    >
                        <CardHeader
                            title="Start an Inspection"
                            as="h2"
                            subtitle="Select an item to start an inspection"
                            actions={
                                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                                    navigator.userAgent,
                                ) && (
                                    <Btn
                                        variant="primary"
                                        buttonSize="lg"
                                        buttonClasses="btn-qr"
                                        buttonText="Scan a QR code"
                                        buttonIcon={<IconQRcode iconClasses="btn-qr-icon" />}
                                        buttonIconPosition="left"
                                        buttonOnClick={() => {
                                            const input = document.createElement('input');
                                            input.type = 'file';
                                            input.accept = 'image/*';
                                            input.capture = 'environment';
                                            input.onchange = (e) => {
                                                const file = (e.target as HTMLInputElement).files?.[0];
                                                if (file) {
                                                    // Handle the captured image here
                                                    console.log('QR code image captured:', file);
                                                    // You can add QR code scanning logic here
                                                }
                                            };
                                            input.click();
                                        }}
                                    />
                                )
                            }
                        />

                        {/* TODO: add a check to see if the company has a start of day and/or a end of day form saved, if so show these 2 tiles with quick links to fill out those reports. */}
                        <div className="home-favorite-forms">
                            <div className="report-cards-toolbar">
                                <p className="report-cards-toolbar-hint">
                                    Select up to {MAX_HOME_DISPLAY_FAVORITES} favorites (inspection or daily service
                                    forms) to display. Star and reorder favorites on the Forms page to change what
                                    appears here.
                                </p>
                                <MultiSelect
                                    className="home-favorite-forms-multiselect"
                                    ariaLabel="Select up to 4 favorites to display"
                                    placeholder="0/4 forms selected"
                                    formatTriggerLabel={(selected) =>
                                        `${selected.length}/${MAX_HOME_DISPLAY_FAVORITES} forms selected`
                                    }
                                    options={favoriteFormOptions}
                                    optionGroups={favoriteFormOptionGroups}
                                    value={homeDisplayFavoriteFormIds}
                                    onChange={handleFavoriteFormsChange}
                                    maxSelections={MAX_HOME_DISPLAY_FAVORITES}
                                    emptyMessage="No published forms available"
                                />
                            </div>

                            <div className="report-cards">
                                {favoriteForms.map((form) => {
                                    const formId = form.id as string;
                                    const isStartOfDay =
                                        formId === startOfDayForm?.id ||
                                        Boolean(form.name?.toLowerCase().startsWith('start of day'));
                                    const isEndOfDay =
                                        formId === endOfDayForm?.id ||
                                        Boolean(form.name?.toLowerCase().startsWith('end of day'));

                                    if (isStartOfDay) {
                                        return (
                                            <Card
                                                key={formId}
                                                variant="card-sm"
                                                className="report-card favorite-form-card"
                                                role="button"
                                                tabIndex={0}
                                                title="Start of Day Report"
                                                onClick={() =>
                                                    openShiftReport(
                                                        form.name || 'start of day',
                                                        'start-of-day',
                                                        formId,
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        openShiftReport(
                                                            form.name || 'start of day',
                                                            'start-of-day',
                                                            formId,
                                                        );
                                                    }
                                                }}
                                            >
                                                <div className="favorite-form-card-icon" aria-hidden="true">
                                                    <img src="/start-of-day-image.png" alt="" className="category-image" />
                                                </div>
                                                <Btn
                                                    buttonAs="span"
                                                    variant="secondary"
                                                    buttonSize="sm"
                                                    buttonClasses="report-card-label"
                                                    buttonText="Start of Day Report"
                                                />
                                            </Card>
                                        );
                                    }

                                    if (isEndOfDay) {
                                        return (
                                            <Card
                                                key={formId}
                                                variant="card-sm"
                                                className="report-card favorite-form-card"
                                                role="button"
                                                tabIndex={0}
                                                title="End of Day Report"
                                                onClick={() =>
                                                    openShiftReport(
                                                        form.name || 'end of day',
                                                        'end-of-day',
                                                        formId,
                                                    )
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        openShiftReport(
                                                            form.name || 'end of day',
                                                            'end-of-day',
                                                            formId,
                                                        );
                                                    }
                                                }}
                                            >
                                                <div className="favorite-form-card-icon" aria-hidden="true">
                                                    <img src="/end-of-day-image.png" alt="" className="category-image" />
                                                </div>
                                                <Btn
                                                    buttonAs="span"
                                                    variant="secondary"
                                                    buttonSize="sm"
                                                    buttonClasses="report-card-label"
                                                    buttonText="End of Day Report"
                                                />
                                            </Card>
                                        );
                                    }

                                    const favoriteLabel = form.name || 'Untitled form';
                                    const favoriteFormImageUrl = formId
                                        ? imageUrls[getHomeFavoriteFormImageKey(formId)]
                                        : undefined;

                                    return (
                                        <Card
                                            key={formId}
                                            variant="card-sm"
                                            className={`report-card favorite-form-card${
                                                favoriteFormInspectionModal.open &&
                                                favoriteFormInspectionModal.form?.id === formId
                                                    ? ' is-active'
                                                    : ''
                                            }`}
                                            role="button"
                                            tabIndex={0}
                                            title={favoriteLabel}
                                            onClick={() => handleFavoriteFormClick(formId)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleFavoriteFormClick(formId);
                                                }
                                            }}
                                        >
                                            <div className="favorite-form-card-icon" aria-hidden="true">
                                                {favoriteFormImageUrl ? (
                                                    <img
                                                        src={favoriteFormImageUrl}
                                                        alt=""
                                                        className="category-image"
                                                    />
                                                ) : (
                                                    <IconClipboard />
                                                )}
                                            </div>
                                            <Btn
                                                buttonAs="span"
                                                variant="secondary"
                                                buttonSize="sm"
                                                buttonClasses="report-card-label"
                                                buttonText={favoriteLabel}
                                            />
                                            <span className="favorite-form-card-meta">
                                                {getHomeFavoriteFormMetaLabel(form)}
                                            </span>
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="toggle-equipment-container">
                            <Btn
                                variant="secondary-outlined"
                                buttonClasses="toggle-equipment"
                                buttonText={`${showEquipmentTiles ? 'Hide' : 'Show'} Equipment List`}
                                buttonIcon={showEquipmentTiles ? <IconAngleUp /> : <IconAngleDown />}
                                buttonIconPosition="right"
                                buttonOnClick={toggleEquipmentTiles}
                                buttonSize="lg"
                            />
                        </div>

                        {/* <div className="equipment-grid"> */}
                        {showEquipmentTiles && (
                            <div className="equipment-toggle-panel">
                                {displayEquipment.length === 0 ? (
                                    <p className="home-favorite-form-empty-equipment">No equipment available.</p>
                                ) : (
                                    <div className="equipment-card-grid">
                                        {displayEquipment.map((item) => {
                                            const inspectHref = getHomeEquipmentInspectHref(item.id, null);

                                            return (
                                                <Card
                                                    key={item.id}
                                                    variant="card-sm"
                                                    className="dashboard-equipment-card"
                                                >
                                                    <a
                                                        href={inspectHref}
                                                        className="dashboard-equipment-card-link"
                                                        title={item.name}
                                                        aria-label={`Inspect ${item.name}`}
                                                        onClick={(event) =>
                                                            handleHomeEquipmentInspectClick(
                                                                event,
                                                                inspectHref,
                                                                navigate,
                                                            )
                                                        }
                                                    >
                                                    <div className="dashboard-card-media">
                                                        <div className="dashboard-card-placeholder" aria-hidden="true">
                                                            <IconEquipment iconClasses="dashboard-card-placeholder-icon" />
                                                        </div>
                                                        {imageUrls[item.id] && (
                                                            <img
                                                                src={imageUrls[item.id]}
                                                                alt=""
                                                                className="dashboard-card-image"
                                                                onError={(e) => {
                                                                    e.currentTarget.remove();
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <span className="dashboard-equipment-card-name">{item.name}</span>
                                                    </a>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                )}
                                {showEquipmentPagination && (
                                    <Pagination
                                        className="equipment-collection-pagination"
                                        currentPage={equipmentPage}
                                        totalPages={equipmentTotalPages}
                                        onPageChange={setEquipmentPage}
                                        ariaLabel="Equipment list pagination"
                                    />
                                )}
                            </div>
                        )}
                    </Card>
                    {/* </div> */}

                    {/* Right Column - Issues & Activity */}
                    <div className="sidebar">
                        <Card variant="card-lg" className="issues-panel">
                            <CardHeader title="Current issues" as="h3" />
                            {dashboardStats.currentIssues.length > 0 ? (
                                <div className="issues">
                                    <div className="issue-item-heading">
                                        <span className="issue-item-heading-name">Equipment</span>
                                        <span className="issue-item-heading-status">Status</span>
                                    </div>
                                    <Scrollable variant="vertical" itemsInView={4} className="issues-scrollable">
                                        {dashboardStats.currentIssues
                                            .slice(0, DASHBOARD_ISSUES_PREVIEW_LIMIT)
                                            .map((issue, index) => (
                                                <div
                                                    key={issue.id ?? index}
                                                    className="issue-item"
                                                    onClick={() => handleIssueClick(issue)}
                                                    role="button"
                                                    tabIndex={0}
                                                    title={`View ${issue.equipmentName || 'issue'}`}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            handleIssueClick(issue);
                                                        }
                                                    }}
                                                >
                                                    <span className="issue-item-name">
                                                        {issue.equipmentName || 'Unknown Equipment'}
                                                    </span>
                                                    <span className="issue-item-status">
                                                        <Badge
                                                            badgeClasses={
                                                                issue.status === 'inop'
                                                                    ? 'badge-danger'
                                                                    : issue.status === 'warn'
                                                                      ? 'badge-warning'
                                                                      : 'badge-info'
                                                            }
                                                            badgeText={
                                                                issue.status === 'inop'
                                                                    ? 'OUT OF SERVICE'
                                                                    : issue.status === 'warn'
                                                                      ? 'WARNING'
                                                                      : 'UNDER REVIEW'
                                                            }
                                                        />
                                                    </span>
                                                </div>
                                            ))}
                                        {dashboardStats.currentIssues.length > DASHBOARD_ISSUES_PREVIEW_LIMIT && (
                                            <div className="issues-view-more">
                                                <Btn
                                                    variant="primary"
                                                    buttonText="View more"
                                                    buttonOnClick={() => navigate('/issues')}
                                                />
                                            </div>
                                        )}
                                    </Scrollable>
                                </div>
                            ) : (
                                <p>No current issues</p>
                            )}
                        </Card>

                        <Card variant="card-lg" className="activity-panel">
                            <CardHeader title="Recent activity" as="h3" />
                            {dashboardStats.recentActivity.length > 0 ? (
                                <Scrollable variant="vertical" itemsInView={4} className="activity-scrollable">
                                    {dashboardStats.recentActivity.map((activity) => (
                                        <div
                                            key={activity.id}
                                            className="activity-item"
                                            onClick={() => handleActivityClick(activity)}
                                            role="button"
                                            tabIndex={0}
                                            title={`View ${activity.title}`}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleActivityClick(activity);
                                                }
                                            }}
                                        >
                                            <div className="user-avatar" aria-hidden="true">
                                                {activityUserPhotoUrls[activity.userId] ? (
                                                    <img
                                                        src={activityUserPhotoUrls[activity.userId]}
                                                        alt={activity.userName}
                                                        className="user-avatar-img"
                                                    />
                                                ) : (
                                                    <span className="user-avatar-initial" aria-hidden="true">
                                                        {getInitials(activity.userName)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="activity-item-info">
                                                <div>
                                                    <span className="activity-item-status">{activity.status}</span>
                                                    <span className="activity-item-time">
                                                        {formatActivityDate(activity.createdAt)}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span className="activity-item-equipment">
                                                        {activity.equipmentName}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </Scrollable>
                            ) : (
                                <p>No recent activity</p>
                            )}
                        </Card>
                    </div>
                </div>
            </div>

            <CustomModal
                visible={isEditCompanyModelOpen}
                size="large"
                header="Edit company"
                onDismiss={() => {
                    setIsEditCompanyModelOpen(false);
                    setCompanyToEdit(null);
                    resetCompanyForm();
                }}
                footer={
                    <div className="modal-footer-actions">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Cancel"
                            buttonOnClick={() => {
                                setIsEditCompanyModelOpen(false);
                                setCompanyToEdit(null);
                                resetCompanyForm();
                            }}
                        />
                        <Btn
                            variant="primary"
                            buttonText="Submit"
                            buttonDisabled={!companyNameInForm.trim()}
                            buttonOnClick={() => {
                                void handleEditFormSubmit({ preventDefault() {} });
                            }}
                        />
                    </div>
                }
            >
                <div className="home-company-fields">
                    <InputField label="Company Name" value={companyNameInForm} onChange={setCompanyNameInForm} />
                    <InputField label="Company Type" value={companyTypeInForm} onChange={setCompanyTypeInForm} />
                    <div className="input-field">
                        <span className="input-field-label" id="home-company-logo-label">
                            Company Logo
                        </span>
                        <ImageUpload
                            labelId="home-company-logo-label"
                            previewUrl={companyLogoPreview || undefined}
                            previewAlt={companyToEdit?.name || 'Company logo'}
                            accept="image/jpeg,image/jpg,image/png"
                            uploadLabel="Choose logo"
                            changeLabel="Change logo"
                            onSelect={(file) => {
                                setCompanyLogoInForm([file]);
                                setCompanyLogoPreview(URL.createObjectURL(file));
                            }}
                        />
                        <p className="home-field-hint">.jpg, .jpeg, or .png file</p>
                    </div>
                    <label className="input-field">
                        <span className="input-field-label">Company Description</span>
                        <textarea
                            className="input-field-control home-company-description"
                            value={companyDescriptionInForm}
                            onChange={(event) => setCompanyDescriptionInForm(event.target.value)}
                        />
                    </label>
                </div>
            </CustomModal>

            <HomeFavoriteFormInspectionModal
                visible={favoriteFormInspectionModal.open}
                form={favoriteFormInspectionModal.form}
                scopedEquipment={scopedEquipment}
                onDismiss={() => setFavoriteFormInspectionModal({ open: false, form: null })}
            />

            <CustomModal
                visible={isSubscriptionExpiredModalOpen}
                onDismiss={() => setIsSubscriptionExpiredModalOpen(false)}
                header="Subscription Expired"
                footer={
                    <div className="modal-footer-actions">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Close"
                            buttonOnClick={() => setIsSubscriptionExpiredModalOpen(false)}
                        />
                        <Btn
                            variant="primary"
                            buttonText="Manage Subscription"
                            buttonOnClick={() => navigate('/manage/subscription')}
                        />
                    </div>
                }
            >
                <p>Company Management Not Available until Subscription is Renewed</p>
            </CustomModal>

            <CustomModal
                visible={shiftReportModal.open}
                onDismiss={() => setShiftReportModal({ ...shiftReportModal, open: false })}
                size="xlarge"
                bodyClassName="home-flow-modal-body"
                header={shiftReportModal.shiftType === 'end-of-day' ? 'End of Day Report' : 'Start of Day Report'}
            >
                {shiftReportModal.open && (
                    <ShiftReportWizard
                        formName={shiftReportModal.formName}
                        formId={shiftReportModal.formId}
                        shiftType={shiftReportModal.shiftType}
                        onInspectionComplete={() => {
                            setShiftReportModal({ ...shiftReportModal, open: false });
                        }}
                        onFinalSubmit={() => {
                            setShiftReportModal({ ...shiftReportModal, open: false });
                            navigate('/report-sent', { replace: true });
                        }}
                    />
                )}
            </CustomModal>
        </Stack>
    );
};
